import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLeague } from '@/contexts/LeagueContext';
import MatchupBoxScore, { type BoxScorePlayer } from '@/components/mlb-beta/MatchupBoxScore';
import MLBPlayerRow from '@/components/mlb-beta/MLBPlayerRow';
import { cn } from '@/lib/utils';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';
import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  ChevronUp,
  Trophy,
  TrendingDown,
  Users,
  UserPlus,
} from 'lucide-react';

/** Build starter slots dynamically from league config */
function buildStarterSlots(config: any): string[] {
  const DEFAULT = ['C', '1B', '2B', 'SS', '3B', 'OF1', 'OF2', 'OF3', 'DH', 'UTIL', 'SP1', 'SP2', 'RP1', 'RP2'];
  if (!config?.roster_mins) return DEFAULT;
  const mins = config.roster_mins as Record<string, number>;
  const pitcherType = config.pitcher_slot_type || 'separate';
  const slots: string[] = [];
  for (const pos of ['C', '1B', '2B', 'SS', '3B', 'OF', 'DH', 'UTIL']) {
    const count = mins[pos] ?? 0;
    if (count <= 0) continue;
    if (count === 1) slots.push(pos);
    else for (let i = 1; i <= count; i++) slots.push(`${pos}${i}`);
  }
  if (pitcherType === 'combined') {
    const pCount = mins['P'] ?? 0;
    if (pCount === 1) slots.push('P');
    else for (let i = 1; i <= pCount; i++) slots.push(`P${i}`);
  } else {
    const spCount = mins['SP'] ?? 0;
    const rpCount = mins['RP'] ?? 0;
    if (spCount === 1) slots.push('SP'); else for (let i = 1; i <= spCount; i++) slots.push(`SP${i}`);
    if (rpCount === 1) slots.push('RP'); else for (let i = 1; i <= rpCount; i++) slots.push(`RP${i}`);
  }
  return slots.length > 0 ? slots : DEFAULT;
}

interface RosterPlayer {
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  slot: string;
  is_starter: boolean;
  trashPoints: number;
  projectedPoints: number;
}

interface MatchupResult {
  id: string;
  week: number;
  opponent_name: string;
  team_score: number;
  opponent_score: number;
  status: string;
  is_home: boolean;
}

interface MLBBetaTeamDetailProps {
  teamId: string;
  onBack: () => void;
  onProposeTrade?: () => void;
}

const MLBBetaTeamDetail = ({ teamId, onBack, onProposeTrade }: MLBBetaTeamDetailProps) => {
  const { activeLeague, teams, myTeam } = useLeague();
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [matchups, setMatchups] = useState<MatchupResult[]>([]);
  const [loading, setLoading] = useState(true);

  const team = teams.find(t => t.id === teamId);
  const isMyTeam = myTeam?.id === teamId;

  useEffect(() => {
    if (!teamId || !activeLeague) return;

    const fetchData = async () => {
      setLoading(true);

      // Fetch roster assignments with player info
      const { data: assignments } = await supabase
        .from('roster_assignments')
        .select('player_id, slot, is_starter, players!inner(full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, headshot_url)')
        .eq('team_id', teamId);

      const playerIds = (assignments || []).map((a: any) => a.player_id);

      // Compute season SAB PTS from player_season_stats + league scoring weights
      const weights = buildScoringWeights(activeLeague.category_config);
      const scoresMap = new Map<string, { total: number; games: number }>();
      if (playerIds.length > 0) {
        const { data: seasonStats } = await supabase
          .from('player_season_stats')
          .select('*')
          .in('player_id', playerIds)
          .eq('season', 2026);
        for (const ss of (seasonStats || [])) {
          const total = calculateTrashPoints(ss as Record<string, number>, weights);
          scoresMap.set(ss.player_id, { total, games: ss.games_played || 1 });
        }
      }

      const rosterPlayers: RosterPlayer[] = (assignments || []).map((a: any) => {
        const scores = scoresMap.get(a.player_id);
        const tp = scores?.total || 0;
        const gp = scores?.games || 1;
        return {
          player_id: a.player_id,
          full_name: a.players.full_name,
          position_abbreviation: a.players.position_abbreviation,
          position_eligibility: a.players.position_eligibility || null,
          mlb_team_abbreviation: a.players.mlb_team_abbreviation,
          headshot_url: a.players.headshot_url,
          slot: a.slot,
          is_starter: a.is_starter,
          trashPoints: tp,
          projectedPoints: tp / gp,
        };
      });

      // Sort: starters first, then by slot
      const STARTER_SLOTS = buildStarterSlots(activeLeague?.category_config);
      rosterPlayers.sort((a, b) => {
        if (a.is_starter !== b.is_starter) return a.is_starter ? -1 : 1;
        const ai = STARTER_SLOTS.indexOf(a.slot);
        const bi = STARTER_SLOTS.indexOf(b.slot);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });

      setRoster(rosterPlayers);

      // Fetch matchups
      const { data: matchupData } = await supabase
        .from('matchups')
        .select('id, week, home_team_id, away_team_id, home_team_name, away_team_name, home_score, away_score, status')
        .eq('league_id', activeLeague.id)
        .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
        .order('week');

      const matchupResults: MatchupResult[] = (matchupData || []).map(m => {
        const isHome = m.home_team_id === teamId;
        return {
          id: m.id,
          week: m.week,
          opponent_name: isHome ? m.away_team_name : m.home_team_name,
          team_score: Number(isHome ? m.home_score : m.away_score),
          opponent_score: Number(isHome ? m.away_score : m.home_score),
          status: m.status,
          is_home: isHome,
        };
      });
      setMatchups(matchupResults);
      setLoading(false);
    };

    fetchData();
  }, [teamId, activeLeague]);

  if (loading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
      </div>
    );
  }

  if (!team) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">Team not found</p>
        </CardContent>
      </Card>
    );
  }

  const STARTER_SLOTS_DISPLAY = buildStarterSlots(activeLeague?.category_config);
  const HITTER_SLOT_PREFIXES = ['C', '1B', '2B', 'SS', '3B', 'OF', 'DH', 'UTIL'];
  const PITCHER_SLOT_PREFIXES = ['SP', 'RP', 'P'];
  const PITCHER_POSITIONS = new Set(['SP', 'RP', 'P']);

  const isHitterSlot = (slot: string) => HITTER_SLOT_PREFIXES.some(p => slot.startsWith(p));
  const isPitcherSlot = (slot: string) => PITCHER_SLOT_PREFIXES.some(p => slot.startsWith(p));
  const isPitcherPosition = (pos: string) => PITCHER_POSITIONS.has(pos);

  const hitterStarterSlots = STARTER_SLOTS_DISPLAY.filter(isHitterSlot);
  const pitcherStarterSlots = STARTER_SLOTS_DISPLAY.filter(isPitcherSlot);

  const starters = roster.filter(p => p.is_starter);
  const bench = roster.filter(p => !p.is_starter && p.slot !== 'IL');
  const ilPlayers = roster.filter(p => p.slot === 'IL');

  const hitterBench = bench.filter(p => !isPitcherPosition(p.position_abbreviation));
  const pitcherBench = bench.filter(p => isPitcherPosition(p.position_abbreviation));

  const starterTP = starters.reduce((s, p) => s + p.trashPoints, 0);
  const starterProj = starters.reduce((s, p) => s + p.projectedPoints, 0);

  const completedMatchups = matchups.filter(m => m.status === 'final' || m.status === 'completed');
  const upcomingMatchups = matchups.filter(m => m.status === 'scheduled' || m.status === 'live');

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold font-heading text-foreground truncate">{team.name}</h1>
          <p className="text-sm text-muted-foreground">
            {team.owner_name} · {team.wins}-{team.losses}{team.ties > 0 ? `-${team.ties}` : ''} · {roster.length} players
          </p>
        </div>
        {!isMyTeam && myTeam && onProposeTrade && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={onProposeTrade}>
            <ArrowLeftRight className="w-3.5 h-3.5" />
            Trade
          </Button>
        )}
      </div>

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/40 border border-border">
          <Trophy className="w-4 h-4 text-primary" />
          <div>
            <p className="text-lg font-bold font-heading text-foreground">{starterTP.toFixed(1)}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Starter TP</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/40 border border-border">
          <TrendingDown className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-lg font-bold font-heading text-foreground">{starterProj >= 0 ? '+' : ''}{starterProj.toFixed(1)}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Proj/G</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/40 border border-border">
          <Users className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-lg font-bold font-heading text-foreground">{roster.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Rostered</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="roster" className="w-full">
        <TabsList className="bg-muted w-full">
          <TabsTrigger value="roster" className="flex-1">Roster</TabsTrigger>
          <TabsTrigger value="matchups" className="flex-1">Matchups</TabsTrigger>
        </TabsList>

        {/* Roster Tab */}
        <TabsContent value="roster" className="space-y-4 mt-4">
          {/* Hitters Section */}
          <Card className="bg-card border-border overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Hitters
              </span>
              <span className="text-xs font-bold text-primary">
                {starters.filter(p => isHitterSlot(p.slot)).reduce((s, p) => s + p.trashPoints, 0).toFixed(1)} TP
              </span>
            </div>
            {hitterStarterSlots.map(slot => {
              const player = starters.find(p => p.slot === slot);
              if (player) {
                return (
                  <MLBPlayerRow
                    key={player.player_id}
                    playerId={player.player_id}
                    name={player.full_name}
                    position={player.position_abbreviation}
                    positionEligibility={player.position_eligibility || undefined}
                    team={player.mlb_team_abbreviation}
                    headshotUrl={player.headshot_url}
                    slot={slot.replace(/\d+$/, '')}
                    statValue={player.trashPoints}
                    statLabel="TP"
                    extraInfo={`PROJ ${player.projectedPoints >= 0 ? '+' : ''}${player.projectedPoints.toFixed(1)}/G`}
                  />
                );
              }
              const slotLabel = slot.replace(/\d+$/, '');
              return (
                <div key={`empty-${slot}`} className="flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-b-0">
                  <Badge variant="outline" className="text-[10px] font-mono w-10 justify-center shrink-0 border-dashed text-muted-foreground">{slotLabel}</Badge>
                  <div className="w-9 h-9 rounded-full bg-muted/40 border border-dashed border-border flex items-center justify-center">
                    <UserPlus className="w-3.5 h-3.5 text-muted-foreground/50" />
                  </div>
                  <span className="text-sm text-muted-foreground italic">Empty</span>
                </div>
              );
            })}
            {hitterBench.length > 0 && (
              <>
                <div className="px-4 py-2 bg-muted/20 border-t border-b border-border">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bench ({hitterBench.length})</span>
                </div>
                {hitterBench.map(player => (
                  <MLBPlayerRow
                    key={player.player_id}
                    playerId={player.player_id}
                    name={player.full_name}
                    position={player.position_abbreviation}
                    positionEligibility={player.position_eligibility || undefined}
                    team={player.mlb_team_abbreviation}
                    headshotUrl={player.headshot_url}
                    slot="BN"
                    statValue={player.trashPoints}
                    statLabel="TP"
                    isStarter={false}
                    extraInfo={`PROJ ${player.projectedPoints >= 0 ? '+' : ''}${player.projectedPoints.toFixed(1)}/G`}
                  />
                ))}
              </>
            )}
          </Card>

          {/* Pitchers Section */}
          <Card className="bg-card border-border overflow-hidden">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Pitchers
              </span>
              <span className="text-xs font-bold text-primary">
                {starters.filter(p => isPitcherSlot(p.slot)).reduce((s, p) => s + p.trashPoints, 0).toFixed(1)} TP
              </span>
            </div>
            {pitcherStarterSlots.map(slot => {
              const player = starters.find(p => p.slot === slot);
              if (player) {
                return (
                  <MLBPlayerRow
                    key={player.player_id}
                    playerId={player.player_id}
                    name={player.full_name}
                    position={player.position_abbreviation}
                    positionEligibility={player.position_eligibility || undefined}
                    team={player.mlb_team_abbreviation}
                    headshotUrl={player.headshot_url}
                    slot={slot.replace(/\d+$/, '')}
                    statValue={player.trashPoints}
                    statLabel="TP"
                    extraInfo={`PROJ ${player.projectedPoints >= 0 ? '+' : ''}${player.projectedPoints.toFixed(1)}/G`}
                  />
                );
              }
              const slotLabel = slot.replace(/\d+$/, '');
              return (
                <div key={`empty-${slot}`} className="flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-b-0">
                  <Badge variant="outline" className="text-[10px] font-mono w-10 justify-center shrink-0 border-dashed text-muted-foreground">{slotLabel}</Badge>
                  <div className="w-9 h-9 rounded-full bg-muted/40 border border-dashed border-border flex items-center justify-center">
                    <UserPlus className="w-3.5 h-3.5 text-muted-foreground/50" />
                  </div>
                  <span className="text-sm text-muted-foreground italic">Empty</span>
                </div>
              );
            })}
            {pitcherBench.length > 0 && (
              <>
                <div className="px-4 py-2 bg-muted/20 border-t border-b border-border">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Bench ({pitcherBench.length})</span>
                </div>
                {pitcherBench.map(player => (
                  <MLBPlayerRow
                    key={player.player_id}
                    playerId={player.player_id}
                    name={player.full_name}
                    position={player.position_abbreviation}
                    positionEligibility={player.position_eligibility || undefined}
                    team={player.mlb_team_abbreviation}
                    headshotUrl={player.headshot_url}
                    slot="BN"
                    statValue={player.trashPoints}
                    statLabel="TP"
                    isStarter={false}
                    extraInfo={`PROJ ${player.projectedPoints >= 0 ? '+' : ''}${player.projectedPoints.toFixed(1)}/G`}
                  />
                ))}
              </>
            )}
          </Card>

          {/* IL */}
          {ilPlayers.length > 0 && (
            <Card className="bg-card border-border overflow-hidden">
              <div className="px-4 py-2.5 bg-destructive/10 border-b border-border">
                <span className="text-xs font-semibold uppercase tracking-wider text-destructive">
                  Injured List ({ilPlayers.length})
                </span>
              </div>
              {ilPlayers.map(player => (
                <MLBPlayerRow
                  key={player.player_id}
                  playerId={player.player_id}
                  name={player.full_name}
                  position={player.position_abbreviation}
                  positionEligibility={player.position_eligibility || undefined}
                  team={player.mlb_team_abbreviation}
                  headshotUrl={player.headshot_url}
                  slot="IL"
                  statValue={player.trashPoints}
                  statLabel="TP"
                  isStarter={false}
                />
              ))}
            </Card>
          )}
        </TabsContent>

        {/* Matchups Tab */}
        <TabsContent value="matchups" className="space-y-4 mt-4">
          {/* Upcoming */}
          {upcomingMatchups.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">Upcoming</h3>
              {upcomingMatchups.map(m => (
                <Card key={m.id} className="bg-card border-border">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted/40 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-muted-foreground">W{m.week}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {m.is_home ? 'vs' : '@'} {m.opponent_name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {m.is_home ? 'Home' : 'Away'}
                        </p>
                      </div>
                    </div>
                    <Badge variant={m.status === 'live' ? 'destructive' : 'secondary'} className="text-[10px]">
                      {m.status === 'live' ? '● LIVE' : 'Scheduled'}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Completed */}
          {completedMatchups.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">Results</h3>
              {completedMatchups.map(m => {
                const won = m.team_score > m.opponent_score;
                const tied = m.team_score === m.opponent_score;
                return (
                  <Card key={m.id} className="bg-card border-border">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold",
                          won ? "bg-primary/20 text-primary" : tied ? "bg-muted/40 text-muted-foreground" : "bg-destructive/20 text-destructive"
                        )}>
                          {won ? 'W' : tied ? 'T' : 'L'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {m.is_home ? 'vs' : '@'} {m.opponent_name}
                          </p>
                          <p className="text-[10px] text-muted-foreground">Week {m.week}</p>
                        </div>
                      </div>
                      <span className={cn(
                        "text-sm font-bold tabular-nums",
                        won ? "text-primary" : tied ? "text-muted-foreground" : "text-destructive"
                      )}>
                        {m.team_score.toFixed(1)} - {m.opponent_score.toFixed(1)}
                      </span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {matchups.length === 0 && (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center text-muted-foreground">
                <p className="text-sm">No matchups scheduled yet.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MLBBetaTeamDetail;
